const GA4_MEASUREMENT_ID =
  import.meta.env?.VITE_GA4_MEASUREMENT_ID?.trim() || 'G-K5LDC3GJH1';

type Gtag = (...args: unknown[]) => void;

let analyticsScriptScheduled = false;

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: Gtag;
    __alMalakiGa4Initialized?: boolean;
  }
}

export function initializeAnalytics() {
  if (typeof window === 'undefined' || !GA4_MEASUREMENT_ID) {
    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function gtag() {
      // Google tag processes queued calls from the native Arguments object.
      window.dataLayer.push(arguments);
    };

  if (window.__alMalakiGa4Initialized) {
    return;
  }

  window.gtag('js', new Date());
  window.gtag('config', GA4_MEASUREMENT_ID, {
    send_page_view: false,
  });
  window.__alMalakiGa4Initialized = true;

  if (analyticsScriptScheduled) return;
  analyticsScriptScheduled = true;

  const interactionEvents = [
    'pointerdown',
    'keydown',
    'focusin',
    'scroll',
  ] as const;
  let fallbackTimer = 0;
  let loadHandler: (() => void) | undefined;
  let firstFrame = 0;
  let secondFrame = 0;
  let idleCallbackId: number | undefined;
  const idleWindow = window as Window & {
    requestIdleCallback?: (
      callback: () => void,
      options?: { timeout?: number },
    ) => number;
    cancelIdleCallback?: (handle: number) => void;
  };
  const cleanupLoadingTriggers = () => {
    window.clearTimeout(fallbackTimer);
    window.cancelAnimationFrame(firstFrame);
    window.cancelAnimationFrame(secondFrame);
    if (idleCallbackId !== undefined) {
      idleWindow.cancelIdleCallback?.(idleCallbackId);
    }
    if (loadHandler) window.removeEventListener('load', loadHandler);
    for (const eventName of interactionEvents) {
      window.removeEventListener(eventName, loadScript);
    }
  };
  const loadScript = () => {
    if (document.querySelector('script[data-al-malaki-ga4]')) {
      cleanupLoadingTriggers();
      return;
    }
    cleanupLoadingTriggers();
    const script = document.createElement('script');
    script.async = true;
    script.dataset.alMalakiGa4 = 'true';
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;
    document.head.appendChild(script);
  };
  const scheduleAfterLoad = () => {
    for (const eventName of interactionEvents) {
      window.addEventListener(eventName, loadScript, {
        once: true,
        passive: true,
      });
    }
    firstFrame = window.requestAnimationFrame(() => {
      secondFrame = window.requestAnimationFrame(() => {
        if (idleWindow.requestIdleCallback) {
          idleCallbackId = idleWindow.requestIdleCallback(loadScript, {
            timeout: 3000,
          });
          fallbackTimer = window.setTimeout(loadScript, 3000);
        } else {
          fallbackTimer = window.setTimeout(loadScript, 1500);
        }
      });
    });
  };

  if (document.readyState === 'complete') {
    scheduleAfterLoad();
  } else {
    loadHandler = scheduleAfterLoad;
    window.addEventListener('load', loadHandler, { once: true });
  }
}

export function trackPageView(path: string) {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
    return;
  }

  const publicPath = getPublicAnalyticsPath(path);
  if (!publicPath) return;

  window.gtag('event', 'page_view', {
    page_location: `${window.location.origin}${publicPath}`,
    page_path: publicPath,
    page_title: document.title,
    send_to: GA4_MEASUREMENT_ID,
  });
}

export function getPublicAnalyticsPath(path: string) {
  const pathname = path.split(/[?#]/, 1)[0] || '/';
  if (/(^|\/)(?:owner|my-atelier|remake|review)(?:\/|$)/i.test(pathname)) {
    return null;
  }
  return pathname;
}

type AnalyticsItem = {
  id: string;
  name?: string;
  quantity?: number;
};

function trackCommerceEvent(
  eventName: 'view_item' | 'add_to_cart' | 'begin_checkout' | 'remake_request',
  items: AnalyticsItem[],
) {
  if (typeof window === 'undefined' || !items.length) return;
  initializeAnalytics();
  if (typeof window.gtag !== 'function') return;

  // Keep payloads intentionally limited to non-personal product identifiers and quantities.
  const safeItems = items
    .filter((item) => typeof item.id === 'string' && item.id.length > 0)
    .map((item) => ({
      item_id: item.id.slice(0, 255),
      ...(item.name ? { item_name: item.name.slice(0, 100) } : {}),
      ...(Number.isFinite(item.quantity) && item.quantity && item.quantity > 0
        ? { quantity: Math.min(Math.floor(item.quantity), 99) }
        : {}),
    }));
  if (!safeItems.length) return;

  window.gtag('event', eventName, {
    items: safeItems,
    send_to: GA4_MEASUREMENT_ID,
  });
}

export function trackProductView(item: AnalyticsItem) {
  trackCommerceEvent('view_item', [item]);
}

export function trackAddToCart(item: AnalyticsItem) {
  trackCommerceEvent('add_to_cart', [
    { ...item, quantity: item.quantity ?? 1 },
  ]);
}

export function trackCheckoutStart(items: AnalyticsItem[]) {
  trackCommerceEvent('begin_checkout', items);
}

export function trackRemakeRequest(item: AnalyticsItem) {
  trackCommerceEvent('remake_request', [item]);
}
